import type { Task } from "~skillBuilder/utils/types";

import { link, tip, title } from "../../contentBlocks";

export const AzMrtSubstitute = {
  name: "Substitute Teacher Considerations",
  uniqueName: "az-mrt-substitute",
  icon: "Lesson",
  durationTimeText: "5 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start Lesson",
      questions: [
        // Question 1.1
        {
          attribute: "az-mrt.sub/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Substitute Teacher Considerations", durationTimeText: "5 min" })}

As a substitute teacher, you face unique challenges when it comes to mandated reporting. This lesson will help you understand these challenges and provide practical guidance for fulfilling your legal obligations while working in different schools with varying policies and procedures.
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "az-mrt.sub/perspective",
          label: "You Bring a Fresh Perspective",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

As substitute teachers, you have unique considerations. You may be the first to notice signs of abuse because you provide a fresh perspective in the classroom. Children may sometimes disclose abuse to you because they see you as a "safe" adult who isn't part of their regular environment.

It's important to document your observations objectively, using the child's exact words when possible. Maintain strict confidentiality about the situation; you should only discuss it with those who need to know as part of the reporting process.

Make sure you follow both state law and the specific protocols of the school district where you're working. And importantly, if you have concerns about a child, make your report before leaving the school for the day. Don't wait until tomorrow or next time you're at that school.

<ul style="margin-left: 16px;">
<li>👂 <strong>Children may feel safe confiding in you</strong> - You're not part of their usual routine so students may view you as a trusted, neutral adult.</li>
<li>📝 <strong>Document your observations objectively</strong> - Use the child's exact words whenever possible.</li>
<li>🔒 <strong>Maintain strict confidentiality</strong> - Only share information with those directly involved in the reporting and investigation process.</li>
<li>⏰ <strong>Report your concerns before leaving the school</strong> - Don't wait until tomorrow or your next assignment.</li>
</ul>

${tip({ text: "Reducing Risk as a Substitute Teacher - Substitute teachers can help reduce the risk of abuse by creating a supportive classroom environment, being approachable, and reinforcing that students can always talk to a trusted adult if they feel unsafe." })}
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "az-mrt.sub/disclosures",
          label: "Responding to Disclosures",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

If a child discloses abuse to you, how you respond is crucial. Remain calm and listen attentively. Believe what the child tells you - false reports by children are rare. Reassure the child that they did the right thing by telling you and that what happened is not their fault. Explain in age-appropriate terms that you're required to tell people who can help keep them safe.

Document the child's exact words as soon as possible after the disclosure. Don't promise to keep the disclosure a secret, as you're legally required to report it. Avoid expressing shock or disgust, which may make the child feel ashamed. Don't ask leading questions or attempt to investigate the situation yourself. And never confront the alleged abuser, as this could compromise the official investigation and potentially put the child at greater risk.

**DO:**

<ul style="margin-left: 16px;">
<li>😌 <strong>Remain calm and listen</strong> - Your composed reaction helps the child feel safe.</li>
<li>🙏 <strong>Believe the child</strong> - Take every disclosure seriously; they rarely lie about abuse.</li>
<li>🛡️ <strong>Explain that you're required to report to keep them safe</strong> - Be honest and supportive.</li>
<li>📝 <strong>Document their exact words</strong> - Write down what they said word-for-word.</li>
<li>❤️ <strong>Reassure them it's not their fault</strong> - Let them know they did the right thing by telling you.</li>
</ul>

**DO NOT:**

<ul style="margin-left: 16px;">
<li>🤐 <strong>Promise to keep it a secret</strong> - You're legally obligated to report.</li>
<li>😱 <strong>Show shock or disgust</strong> - This can make the child feel ashamed or scared.</li>
<li>❓ <strong>Ask leading questions</strong> - Avoid influencing their answers; stick to open-ended listening.</li>
<li>🕵️‍♂️ <strong>Try to investigate</strong> - Your role is to report, not to verify.</li>
<li>🚪 <strong>Confront the alleged abuser</strong> - This can put you and the child in danger.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "az-mrt.sub/scenarios",
          label: "Scenarios",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

Review the Scenarios below and determine how to respond in each situation.

✅ **Scenario 1** - A student comes to class with multiple bruises on their arms and appears withdrawn. This combination of physical and behavioral indicators gives reasonable cause to suspect abuse.

✅ **Scenario 2** - A child tells you, "My mom's boyfriend touches me in ways that make me uncomfortable." This direct disclosure requires immediate reporting. Remember to document the child's exact words.

✅ **Scenario 3** - A student frequently comes to school hungry, unwashed, and in dirty clothes. These are potential indicators of neglect that warrant reporting.

In all these scenarios, you have reasonable cause to believe abuse or neglect may be occurring, which triggers your legal obligation to report to the Arizona DCS Hotline: 1-888-SOS-CHILD (1-888-767-2445).
`,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 5.1
        {
          attribute: "az-mrt.sub/resources",
          label: "Resources",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `

Here are some important resources for you as a mandated reporter. The Arizona DCS Hotline: 1-888-SOS-CHILD (1-888-767-2445) is your primary reporting resource, available 24/7. The Arizona Department of Child Safety website provides additional information about child protection in Arizona. The Arizona Department of Education offers additional resources specifically for educators.

Swing Education is available to answer questions. Your specific contact for that assistance is support@swingeducation.com. Additionally, each school district where you work will have its own resources and protocols, which are available upon request.

📞 **Arizona DCS Hotline:** 1-888-SOS-CHILD (1-888-767-2445)

<div style="margin-bottom: 8px;">
${link({ text: "AZ Department of Child Safety", url: "https://dcs.az.gov/report-child-abuse" })}
</div>

<div style="margin-bottom: 8px;">
${link({ text: "AZ Department of Education", url: "https://www.azed.gov" })}
</div>

<div style="margin-bottom: 8px;">
${link({ text: "Prevent Child Abuse Arizona", url: "https://pcaaz.org" })}
</div>

<div style="margin-bottom: 8px;">
${link({ text: "✉️ Swing Education Support", url: "mailto:support@swingeducation.com" })}
</div>

<div style="margin-bottom: 8px;">
${link({ text: "📑Arizona DCS Mandated Reporter Training Workbook for Educators (for further reading)", url: "https://dcs.az.gov/sites/default/files/documents/files/StatewideMandatedReporterTraining-Education-PW-11.23.2022.pdf" })}
</div>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
